"use server"

import { sdk } from "@lib/config"
import medusaError, { LocalizableError, parseCodedMessage } from "@lib/util/medusa-error"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
} from "./cookies"
import { getRegion } from "./regions"
import { getMedusaLocale } from "@lib/util/locale"

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart(cartId?: string, fields?: string) {
  try {
    const id = cartId || (await getCartId())
    // shipping_methods.data carries packeta pickup-point details and is required
    // for restoring the chosen point on a checkout-page reload.
    fields ??= "*items, *region, *items.product, *items.variant, *items.thumbnail, *items.metadata, +items.total, +subtotal, +total, +tax_total, +shipping_total, +discount_total, *promotions, +shipping_methods.name, +shipping_methods.shipping_option_id, +shipping_methods.data"

    if (!id) {
      return null
    }

    const headers = {
      ...(await getAuthHeaders()),
    }

    const next = {
      ...(await getCacheOptions("carts")),
    }

    const locale = await getMedusaLocale()

    const response = await sdk.client
      .fetch<HttpTypes.StoreCartResponse>(`/store/carts/${id}`, {
        method: "GET",
        query: {
          fields,
          locale,
        },
        headers,
        next,
        cache: "no-store",
      })
      .catch(() => null)

    if (!response) return null

    const cart = response.cart

    // Enrich cart items with product thumbnails if missing
    if (cart?.items?.length) {
      for (const item of cart.items) {
        if (!item.thumbnail && item.product_id) {
          const product = (item as any).product
          if (product) {
            ;(item as any).thumbnail =
              product.thumbnail || product.images?.[0]?.url || null
          }
        }
      }
    }

    return cart
  } catch (error) {
    console.error("Error retrieving cart:", error)
    return null
  }
}

export async function getOrSetCart(countryCode: string) {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  let cart = await retrieveCart(undefined, 'id,region_id')

  const headers = {
    ...(await getAuthHeaders()),
  }

  if (!cart) {
    const cartResp = await sdk.store.cart.create(
      { region_id: region.id },
      {},
      headers
    )
    cart = cartResp.cart

    await setCartId(cart.id)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag, "max")
  }

  if (cart && cart?.region_id !== region.id) {
    await sdk.store.cart.update(cart.id, { region_id: region.id }, {}, headers)
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag, "max")
  }

  return cart
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found, please create one before updating")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, data, {}, headers)
    .then(async ({ cart }: { cart: HttpTypes.StoreCart }) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")

      return cart
    })
    .catch(medusaError)
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}) {
  if (!variantId) {
    throw new Error("Missing variant ID when adding to cart")
  }

  const cart = await getOrSetCart(countryCode)

  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .createLineItem(
      cart.id,
      {
        variant_id: variantId,
        quantity,
      },
      {},
      headers
    )
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")
    })
    .catch(medusaError)
}

/**
 * Add rental item to cart
 * Uses POST /store/cart/:id/rental API
 */
export async function addRentalToCart({
  variantId,
  rentalPeriod,
  rentalUnit,
  startDate,
  quantity,
  countryCode,
}: {
  variantId: string
  rentalPeriod: number
  rentalUnit: string
  startDate: string
  quantity: number
  countryCode: string
}) {
  if (!variantId) {
    throw new Error("Missing variant ID when adding rental to cart")
  }

  const cart = await getOrSetCart(countryCode)

  if (!cart) {
    throw new Error("Error retrieving or creating cart")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.client
    .fetch<{ cart: HttpTypes.StoreCart; rentalMetadata: any }>(
      `/store/cart/${cart.id}/rental`,
      {
        method: "POST",
        body: {
          variantId,
          rentalPeriod,
          rentalUnit,
          startDate,
          quantity,
        },
        headers,
      }
    )
    .then(async (response) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")

      return response
    })
    .catch((error: any) => {
      // Medusa JS SDK's FetchError keeps ONLY response.body.message
      // (everything else from the response body is dropped). So our
      // backend encodes errorCode as `ERR_<code>:<human text>` prefix
      // inside `message` — parseCodedMessage strips it (shared with
      // medusaError, which covers every other cart path).
      const raw =
        error?.message || error?.body?.message || error?.body?.error || ""
      const parsed = parseCodedMessage(raw)
      const message = parsed.message || "Failed to add rental to cart"
      console.error("[addRentalToCart] Error:", message, error)
      throw new LocalizableError(message, parsed.errorCode, parsed.errorParams)
    })
}

export async function updateLineItem({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when updating line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when updating line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .updateLineItem(cartId, lineId, { quantity }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")
    })
    .catch(medusaError)
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when deleting line item")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when deleting line item")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.cart
    .deleteLineItem(cartId, lineId, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")
    })
    .catch(medusaError)
}

export async function setShippingMethod({
  cartId,
  shippingMethodId,
  data,
}: {
  cartId: string
  shippingMethodId: string
  data?: Record<string, unknown>
}) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  try {
    await sdk.store.cart
      .addShippingMethod(cartId, { option_id: shippingMethodId, data }, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag, "max")
  } catch (error: any) {
    // Extract meaningful error from SDK response
    const message = error?.body?.message
      || error?.message
      || "Failed to set shipping method"
    console.error("[setShippingMethod] Error:", message, error)
    throw new Error(message === "An unknown error occurred." ? "Failed to set shipping method. Check fulfillment provider configuration." : message)
  }
}

export async function initiatePaymentSession(
  cart: HttpTypes.StoreCart,
  data: HttpTypes.StoreInitializePaymentSession
) {
  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.payment
    .initiatePaymentSession(cart, data, {}, headers)
    .then(async (resp) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")
      return resp
    })
    .catch(medusaError)
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.cart
    .update(cartId, { promo_codes: codes }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")
    })
    .catch(medusaError)
}

export async function applyGiftCard(code: string) {
  throw new Error("Gift card functionality not yet implemented")
}

export async function removeDiscount(code: string) {
  throw new Error("Discount removal not yet implemented")
}

export async function removeGiftCard(codeToRemove: string, giftCards: any[]) {
  throw new Error("Gift card removal not yet implemented")
}

export async function submitPromotionForm(
  currentState: unknown,
  formData: FormData
) {
  const code = formData.get("code") as string
  try {
    await applyPromotions([code])
  } catch (e: any) {
    return e.message
  }
}

// TODO: Pass a POJO instead of a form entity here
export async function setAddresses(currentState: unknown, formData: FormData) {
  try {
    if (!formData) {
      throw new Error("No form data found when setting addresses")
    }
    const cartId = await getCartId()
    if (!cartId) {
      throw new Error("No existing cart found when setting addresses")
    }

    const data = {
      shipping_address: {
        first_name: formData.get("shipping_address.first_name"),
        last_name: formData.get("shipping_address.last_name"),
        address_1: formData.get("shipping_address.address_1"),
        address_2: "",
        company: formData.get("shipping_address.company"),
        postal_code: formData.get("shipping_address.postal_code"),
        city: formData.get("shipping_address.city"),
        country_code: formData.get("shipping_address.country_code"),
        province: formData.get("shipping_address.province"),
        phone: formData.get("shipping_address.phone"),
      },
      email: formData.get("email"),
    } as any

    const sameAsBilling = formData.get("same_as_billing")
    if (sameAsBilling === "on") data.billing_address = data.shipping_address

    if (sameAsBilling !== "on")
      data.billing_address = {
        first_name: formData.get("billing_address.first_name"),
        last_name: formData.get("billing_address.last_name"),
        address_1: formData.get("billing_address.address_1"),
        address_2: "",
        company: formData.get("billing_address.company"),
        postal_code: formData.get("billing_address.postal_code"),
        city: formData.get("billing_address.city"),
        country_code: formData.get("billing_address.country_code"),
        province: formData.get("billing_address.province"),
        phone: formData.get("billing_address.phone"),
      }
    await updateCart(data)

    // Persist this checkout address (and phone) on the customer profile
    // when the shopper opted in. Best-effort — failure here must not
    // block checkout, so we swallow errors and continue.
    if (formData.get("save_to_profile") === "on") {
      try {
        const { retrieveCustomer, addCustomerAddress, updateCustomer } =
          await import("./customer")
        const customer = await retrieveCustomer().catch(() => null)
        if (customer) {
          const phone = (data.shipping_address.phone as string) || ""
          // Save the shipping address as a new entry on the customer.
          // Mark it default-shipping when the customer has no addresses
          // yet so the next checkout pre-selects it.
          const isFirstAddress = !customer.addresses?.length
          const addrForm = new FormData()
          for (const [k, v] of Object.entries(data.shipping_address)) {
            if (v != null) addrForm.set(k, v as string)
          }
          await addCustomerAddress(
            {
              isDefaultShipping: isFirstAddress,
              isDefaultBilling: isFirstAddress,
            },
            addrForm
          ).catch(() => null)

          // Sync phone onto the customer record only if not already set.
          if (phone && !customer.phone) {
            await updateCustomer({ phone }).catch(() => null)
          }
        }
      } catch {
        // never block checkout on profile persistence
      }
    }
  } catch (e: any) {
    return e.message
  }

  // Delivery was chosen before this step, so contacts lead to payment.
  redirect(
    `/${formData.get("shipping_address.country_code")}/checkout?step=payment`
  )
}

/**
 * Places an order for a cart. If no cart ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to place an order for.
 * @returns The cart object if the order was successful, or null if not.
 */
export async function placeOrder(cartId?: string) {
  const id = cartId || (await getCartId())

  if (!id) {
    throw new Error("No existing cart found when placing an order")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Stamp the shopper's UI locale onto the cart so the order subscribers
  // (welcome / order_confirmed / shipping notifications) localise emails to
  // the language the user actually used in the storefront — not whatever
  // locale was previously cached on the customer record (e.g. from portal).
  try {
    const { getLocale } = await import("next-intl/server")
    const uiLocale = await getLocale().catch(() => null)
    if (uiLocale) {
      await sdk.store.cart
        .update(id, { metadata: { locale: uiLocale } }, {}, headers)
        .catch(() => null)
    }
  } catch {
    // next-intl may not be available in some server contexts — non-blocking
  }

  let cartRes: Awaited<ReturnType<typeof sdk.store.cart.complete>> | undefined
  try {
    cartRes = await sdk.store.cart
      .complete(id, {}, headers)
      .then(async (res) => {
        const cartCacheTag = await getCacheTag("carts")
        revalidateTag(cartCacheTag, "max")
        return res
      })
  } catch (err) {
    // Surface the real backend message to the UI instead of letting a
    // secondary "cannot read .cart of undefined" mask it.
    throw medusaError(err)
  }

  if (cartRes?.type === "order") {
    const countryCode =
      cartRes.order.shipping_address?.country_code?.toLowerCase()

    const orderCacheTag = await getCacheTag("orders")
    revalidateTag(orderCacheTag, "max")

    removeCartId()
    redirect(`/${countryCode}/order/${cartRes?.order.id}/confirmed`)
  }

  return cartRes?.cart ?? null
}

/**
 * Updates the countrycode param and revalidates the regions cache
 * @param regionId
 * @param countryCode
 */
export async function updateRegion(countryCode: string, currentPath: string) {
  const cartId = await getCartId()
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  if (cartId) {
    await updateCart({ region_id: region.id })
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag, "max")
  }

  const regionCacheTag = await getCacheTag("regions")
  revalidateTag(regionCacheTag, "max")

  const productsCacheTag = await getCacheTag("products")
  revalidateTag(productsCacheTag, "max")

  redirect(`/${countryCode}${currentPath}`)
}

export async function listCartOptions() {
  const cartId = await getCartId()
  const headers = {
    ...(await getAuthHeaders()),
  }
  const next = {
    ...(await getCacheOptions("shippingOptions")),
  }

  return await sdk.client.fetch<{
    shipping_options: HttpTypes.StoreCartShippingOption[]
  }>("/store/shipping-options", {
    query: { cart_id: cartId },
    next,
    headers,
    cache: "no-store",
  })
}

/**
 * Set return shipping method for rental items
 *
 * Since Medusa v2 only allows ONE shipping method per cart, return shipping
 * is added as a LINE ITEM instead of a shipping method.
 *
 * This calls POST /store/cart/:id/return-shipping API which:
 * 1. Creates/finds a "Return Shipping Service" product
 * 2. Adds it as a line item with the shipping cost as unit_price
 * 3. Stores metadata for UI display
 */
export async function setReturnShippingMethod({
  shippingMethodId,
  shippingMethodName,
  shippingMethodCost = 0,
  variantId,
  productId,
}: {
  shippingMethodId: string
  shippingMethodName: string
  shippingMethodCost?: number
  variantId?: string
  productId?: string
}) {
  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("No existing cart found when setting return shipping")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Add return shipping product as a line item (replaces any existing)
  await sdk.client.fetch(`/store/cart/${cartId}/return-shipping`, {
    method: "POST",
    body: {
      variantId: variantId || shippingMethodId,
      productId: productId || shippingMethodId,
      productTitle: shippingMethodName,
      unitPrice: shippingMethodCost,
    },
    headers,
  })

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag, "max")

  return retrieveCart(cartId)
}

/**
 * Update rental line item in cart
 * Uses PUT /store/cart/:id/rental/:lineId API
 */
export async function updateRentalLineItem({
  lineId,
  rentalPeriod,
  rentalUnit,
  startDate,
}: {
  lineId: string
  rentalPeriod?: number
  rentalUnit?: "day" | "week" | "month"
  startDate?: string
}) {
  if (!lineId) {
    throw new Error("Missing line item ID when updating rental")
  }

  const cartId = await getCartId()

  if (!cartId) {
    throw new Error("Missing cart ID when updating rental")
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.client
    .fetch<{ cart: HttpTypes.StoreCart; rentalMetadata: any }>(
      `/store/cart/${cartId}/rental/${lineId}`,
      {
        method: "PUT",
        body: {
          ...(rentalPeriod !== undefined && { rentalPeriod }),
          ...(rentalUnit !== undefined && { rentalUnit }),
          ...(startDate !== undefined && { startDate }),
        },
        headers,
      }
    )
    .then(async (response) => {
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag, "max")

      const fulfillmentCacheTag = await getCacheTag("fulfillment")
      revalidateTag(fulfillmentCacheTag, "max")

      return response
    })
    .catch(medusaError)
}
